import { useState, RefObject, useEffect } from 'react';
import { useToast } from "@/components/ui/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";

interface UseChartCopyParams {
  svgRef: RefObject<SVGSVGElement>;
}

export const useChartCopy = ({ svgRef }: UseChartCopyParams) => {
  const [copying, setCopying] = useState(false);
  const [pngUrl, setPngUrl] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const { toast } = useToast();

  // Helper function to convert CSS variables to actual colors
  const replaceCssVariables = (svgElement: SVGElement) => {
    // Replace all data-fill-variable attributes with actual computed colors
    const elementsWithFillVar = svgElement.querySelectorAll('[data-fill-variable]');
    elementsWithFillVar.forEach(el => {
      const fillVarName = el.getAttribute('data-fill-variable');
      if (fillVarName) {
        // Set the explicit color (already defined in the fill attribute as fallback)
        // Keep the existing fill value
      }
    });

    // Also check for stroke attributes with CSS variables
    const elementsWithStrokeVar = svgElement.querySelectorAll('[data-stroke-variable]');
    elementsWithStrokeVar.forEach(el => {
      const strokeVarName = el.getAttribute('data-stroke-variable');
      if (strokeVarName) {
        // Keep the existing stroke value
      }
    });

    // Add font styles inline to each text element
    const textElements = svgElement.querySelectorAll('text');
    const fontFamily = "'Inter', 'Helvetica', 'Arial', sans-serif";
    textElements.forEach(el => {
      if (!el.getAttribute('font-family')) {
        el.setAttribute('font-family', fontFamily);
      }
    });

    // Clean up any data attributes that aren't needed in the final SVG
    const allElements = svgElement.querySelectorAll('*');
    allElements.forEach(el => {
      const attributes = Array.from(el.attributes);
      attributes.forEach(attr => {
        if (attr.name.startsWith('data-')) {
          el.removeAttribute(attr.name);
        }
      });
      
      // Remove class attribute as it won't work outside the app
      if (el.hasAttribute('class')) {
        el.removeAttribute('class');
      }
    });

    return svgElement;
  };

  // Function to convert SVG to PNG and return URL
  const convertSvgToPng = async (): Promise<string> => {
    if (!svgRef.current) throw new Error('SVG reference is not available');
    
    try {
      // Get the SVG element
      const svg = svgRef.current;
      
      // Clone the SVG to avoid modifying the original
      const svgClone = svg.cloneNode(true) as SVGSVGElement;
      
      // Make sure SVG has explicit dimensions
      const width = svg.getAttribute('width') || '320';
      const height = svg.getAttribute('height') || '400';
      svgClone.setAttribute('width', width);
      svgClone.setAttribute('height', height);
      
      // Add XML namespace which is required for SVG to render correctly in image
      svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      
      // Embed Google Fonts
      const style = document.createElementNS('http://www.w3.org/2000/svg', 'style');
      style.textContent = `
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap');
        text {
          font-family: 'Inter', 'Helvetica', 'Arial', sans-serif;
        }
      `;
      
      // Find defs element or create one
      let defsElement = svgClone.querySelector('defs');
      if (!defsElement) {
        defsElement = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
        svgClone.insertBefore(defsElement, svgClone.firstChild);
      }
      
      // Add style to defs
      defsElement.appendChild(style);
      
      // Set white background explicitly
      const bgRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      bgRect.setAttribute('width', '100%');
      bgRect.setAttribute('height', '100%');
      bgRect.setAttribute('fill', '#1A1A1A');
      svgClone.insertBefore(bgRect, svgClone.firstChild);
      
      // Replace CSS variables with actual colors
      replaceCssVariables(svgClone);
      
      // Convert SVG to string with all namespaces included
      const svgData = new XMLSerializer().serializeToString(svgClone);
      
      // Create SVG blob with proper MIME type
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);
      
      // Create Image element to render SVG
      const img = new Image();
      img.src = svgUrl;
      
      // Wait for image to load before drawing on canvas
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = (error) => {
          console.error('Error loading SVG image:', error);
          reject(new Error('Failed to load SVG image'));
        };
      });
      
      // Create canvas with dimensions matching SVG
      const canvas = document.createElement('canvas');
      const widthNum = parseInt(width);
      const heightNum = parseInt(height);
      
      // Higher resolution for better quality
      const scale = 2;
      canvas.width = widthNum * scale;
      canvas.height = heightNum * scale;
      
      // Get canvas context and set background
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }
      
      // Fill with dark background (matching chart background)
      ctx.fillStyle = '#1A1A1A';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Scale for higher resolution
      ctx.scale(scale, scale);
      
      // Draw SVG image on canvas
      ctx.drawImage(img, 0, 0, widthNum, heightNum);
      
      // Convert canvas to PNG data URL
      const pngDataUrl = canvas.toDataURL('image/png');
      
      // Clean up by revoking object URL
      URL.revokeObjectURL(svgUrl);
      
      return pngDataUrl;
      
    } catch (err) {
      console.error('Error converting SVG to PNG:', err);
      throw err;
    }
  };

  // Convert SVG to PNG when the component mounts
  useEffect(() => {
    const generatePng = async () => {
      try {
        if (svgRef.current) {
          // Small delay to ensure SVG is fully rendered
          setTimeout(async () => {
            const url = await convertSvgToPng();
            setPngUrl(url);
          }, 100);
        }
      } catch (error) {
        console.error('Error generating PNG:', error);
      }
    };

    generatePng();
  }, [svgRef.current]); // Only run when SVG ref is available

  const handleCopyChart = async () => {
    if (!pngUrl) {
      toast({
        variant: "destructive",
        title: "Error copying chart",
        description: "Image is not ready yet. Please try again."
      });
      return;
    }
    
    setCopying(true);
    
    try {
      // Get PNG blob from the data URL
      const response = await fetch(pngUrl);
      const blob = await response.blob();
      
      // Use Clipboard API to copy the image
      const item = new ClipboardItem({ 'image/png': blob });
      await navigator.clipboard.write([item]);
      
      toast({
        title: "Copied to clipboard",
        description: "The chart image has been copied to your clipboard"
      });
    } catch (err) {
      console.error('Error copying chart:', err);
      toast({
        variant: "destructive",
        title: "Error copying chart",
        description: "Your browser may not support this feature."
      });
    } finally {
      setCopying(false);
    }
  };

  return {
    copying,
    handleCopyChart,
    pngUrl,
    isMobile
  };
};
